import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import { Alert, Box, Grid, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useLoaderData } from "react-router-dom";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import {
  CompanyAvatar,
  ComplianceItem,
  DocumentRow,
  InfoCard,
  InfoField,
  MapPlaceholder,
  StatCard,
  StatusChip,
  contractorUi,
} from "./components/contractorInfoUi";

const statusMeta = {
  0: { tone: "default", text: "نامشخص" },
  1: { tone: "warning", text: "در حال بررسی" },
  2: { tone: "success", text: "تأیید شده" },
  3: { tone: "error", text: "رد شده" },
  4: { tone: "default", text: "تعلیق شده" },
  Pending: { tone: "warning", text: "در حال بررسی" },
  Accepted: { tone: "success", text: "تأیید شده" },
  Rejected: { tone: "error", text: "رد شده" },
  Suspended: { tone: "default", text: "تعلیق شده" },
};

const pick = (obj, ...keys) => {
  if (!obj) return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null && obj[key] !== "") {
      return obj[key];
    }
  }
  return undefined;
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("fa-IR");
};

const getFileUrl = (relativePath, baseUrl) => {
  if (!relativePath) return null;
  if (String(relativePath).startsWith("http")) return relativePath;
  const root = (baseUrl || window.globalConfig?.API_URL || "").replace(/\/$/, "");
  if (!root) return relativePath;
  return `${root}/${String(relativePath).replace(/^\//, "")}`;
};

const buildDocuments = (contractor, baseUrl, isApproved) => {
  const items = [
    {
      name: "تصویر کارت ملی",
      url: pick(contractor, "nationalCardImageUrl", "NationalCardImageUrl"),
    },
    {
      name: "مجوز شرکت",
      url: pick(contractor, "companyLicenseImageUrl", "CompanyLicenseImageUrl"),
    },
    {
      name: "بیمه مسئولیت",
      url: pick(contractor, "insuranceDocumentUrl", "InsuranceDocumentUrl"),
    },
    {
      name: "گواهینامه صلاحیت",
      url: pick(contractor, "certificateUrl", "CertificateUrl"),
    },
  ];

  return items.map((item) => ({
    ...item,
    href: getFileUrl(item.url, baseUrl),
    statusLabel: item.url ? (isApproved ? "تأیید شده" : "بارگذاری شده") : "بارگذاری نشده",
    statusTone: item.url ? (isApproved ? "success" : "primary") : "default",
  }));
};

const ContractorInfoInformationPage = () => {
  const data = useLoaderData();

  if (!data?.contractorInfo) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: contractorUi.background, minHeight: "100%" }}>
        <BreadcrumbsComponent />
        <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
          {data?.errorMessage || "اطلاعات پیمانکار یافت نشد"}
        </Alert>
      </Box>
    );
  }

  const contractor = data.contractorInfo;
  const baseUrl = data.baseUrl || "";
  const statusKey = pick(contractor, "contractorInfoStatus", "ContractorInfoStatus") ?? 0;
  const status = statusMeta[statusKey] || statusMeta[0];
  const isApproved = statusKey === 2 || statusKey === "Accepted";
  const isActive = Boolean(pick(contractor, "isActive", "IsActive"));

  const firstName = pick(contractor, "firstName", "FirstName");
  const lastName = pick(contractor, "lastName", "LastName");
  const fullName = [firstName, lastName].filter(Boolean).join(" ") || "پیمانکار";
  const companyName = pick(contractor, "companyName", "CompanyName") || "شرکت پیمانکاری";
  const address = pick(contractor, "address", "Address");
  const grade = pick(contractor, "grade", "Grade");
  const workExperience = pick(contractor, "workExperience", "WorkExperience");
  const specialties = pick(contractor, "specialties", "Specialties");
  const documents = buildDocuments(contractor, baseUrl, isApproved);

  const complianceItems = [
    {
      label: "مجوز فعالیت",
      ok: Boolean(pick(contractor, "companyLicenseImageUrl", "CompanyLicenseImageUrl")),
    },
    {
      label: "گواهی صلاحیت",
      ok: Boolean(pick(contractor, "certificateUrl", "CertificateUrl")),
    },
    {
      label: "بیمه مسئولیت",
      ok: Boolean(pick(contractor, "insuranceDocumentUrl", "InsuranceDocumentUrl")),
    },
    {
      label: "مدارک هویتی",
      ok: Boolean(pick(contractor, "nationalCardImageUrl", "NationalCardImageUrl")),
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: contractorUi.background, minHeight: "100%" }}>
      <BreadcrumbsComponent />

      {/* Header */}
      <Box
        sx={{
          mt: 2,
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: `${contractorUi.radius}px`,
          border: `1px solid ${contractorUi.border}`,
          bgcolor: contractorUi.card,
          boxShadow: contractorUi.shadow,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2.5}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography
                variant="h5"
                sx={{ fontWeight: 800, color: contractorUi.text, fontSize: { xs: "1.25rem", md: "1.5rem" } }}
              >
                اطلاعات پیمانکار
              </Typography>
              <StatusChip label={status.text} tone={status.tone} />
              <StatusChip
                label={isActive ? "فعال" : "غیرفعال"}
                tone={isActive ? "success" : "default"}
              />
            </Stack>
            <Typography sx={{ mt: 1, fontWeight: 700, color: contractorUi.text, fontSize: "1.05rem" }}>
              {fullName}
            </Typography>
            <Typography sx={{ mt: 0.5, color: contractorUi.muted, lineHeight: 1.8 }}>
              {companyName}
              {specialties ? ` · ${specialties}` : " · پیمانکار نصب و اجرای نیروگاه خورشیدی"}
            </Typography>
          </Box>
          <CompanyAvatar name={companyName} />
        </Stack>
      </Box>

      {/* KPI */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="سابقه فعالیت"
            value={workExperience ? `${workExperience} سال` : "—"}
            icon={<WorkOutlineRoundedIcon />}
            accent={contractorUi.primary}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="امتیاز عملکرد"
            value={grade ?? "—"}
            icon={<StarRoundedIcon />}
            accent="#F59E0B"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="مدارک بارگذاری‌شده"
            value={`${documents.filter((d) => d.href).length} از ${documents.length}`}
            icon={<FolderOpenRoundedIcon />}
            accent="#0EA5E9"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="وضعیت همکاری"
            value={isApproved ? "تأیید شده" : status.text}
            icon={<BoltRoundedIcon />}
            accent={isApproved ? contractorUi.success : "#94A3B8"}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        {/* Personal */}
        <Grid item xs={12} md={6}>
          <InfoCard
            title="اطلاعات شخصی"
            subtitle="هویت و مشخصات فردی پیمانکار"
            icon={<PersonOutlineRoundedIcon />}
          >
            <InfoField
              icon={<PersonOutlineRoundedIcon fontSize="small" />}
              label="نام و نام خانوادگی"
              value={fullName}
            />
            <InfoField
              icon={<BadgeOutlinedIcon fontSize="small" />}
              label="کد ملی"
              value={pick(contractor, "nationalCode", "NationalCode")}
            />
            <InfoField
              icon={<CakeOutlinedIcon fontSize="small" />}
              label="تاریخ تولد"
              value={formatDate(pick(contractor, "birthDate", "BirthDate"))}
            />
            <InfoField
              icon={<PhoneIphoneOutlinedIcon fontSize="small" />}
              label="موبایل"
              value={pick(contractor, "mobile", "Mobile")}
            />
            <InfoField
              icon={<EmailOutlinedIcon fontSize="small" />}
              label="ایمیل"
              value={pick(contractor, "email", "Email")}
            />
          </InfoCard>
        </Grid>

        {/* Contact */}
        <Grid item xs={12} md={6}>
          <InfoCard
            title="اطلاعات تماس"
            subtitle="راه‌های ارتباطی ثبت‌شده"
            icon={<PhoneOutlinedIcon />}
          >
            <InfoField
              icon={<PhoneIphoneOutlinedIcon fontSize="small" />}
              label="موبایل"
              value={pick(contractor, "mobile", "Mobile")}
            />
            <InfoField
              icon={<PhoneOutlinedIcon fontSize="small" />}
              label="تلفن"
              value={pick(contractor, "telephon", "Telephon")}
            />
            <InfoField
              icon={<EmailOutlinedIcon fontSize="small" />}
              label="ایمیل"
              value={pick(contractor, "email", "Email")}
            />
            <InfoField
              icon={<BusinessRoundedIcon fontSize="small" />}
              label="تلفن شرکت"
              value={pick(contractor, "companyPhone", "CompanyPhone")}
            />
            <InfoField
              icon={<BusinessRoundedIcon fontSize="small" />}
              label="آدرس"
              value={address}
            />
          </InfoCard>
        </Grid>

        {/* Company */}
        <Grid item xs={12}>
          <InfoCard
            title="اطلاعات شرکت"
            subtitle="مشخصات حقوقی و ثبتی پیمانکار"
            icon={<BusinessRoundedIcon />}
          >
            <Grid container spacing={1}>
              <Grid item xs={12} md={4}>
                <InfoField
                  icon={<BusinessRoundedIcon fontSize="small" />}
                  label="نام شرکت"
                  value={companyName}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoField
                  icon={<BadgeOutlinedIcon fontSize="small" />}
                  label="شماره ثبت شرکت"
                  value={pick(
                    contractor,
                    "companyRegistrationNumber",
                    "CompanyRegistrationNumber"
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoField
                  icon={<BadgeOutlinedIcon fontSize="small" />}
                  label="شناسه ملی شرکت"
                  value={pick(contractor, "companyNationalId", "CompanyNationalId")}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoField
                  icon={<WorkOutlineRoundedIcon fontSize="small" />}
                  label="حوزه فعالیت"
                  value={specialties || "نصب و اجرای نیروگاه خورشیدی"}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoField
                  icon={<BusinessRoundedIcon fontSize="small" />}
                  label="استان / شهر"
                  value={[
                    pick(contractor, "province", "Province"),
                    pick(contractor, "city", "City"),
                  ]
                    .filter(Boolean)
                    .join(" / ") || "—"}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoField
                  icon={<BadgeOutlinedIcon fontSize="small" />}
                  label="کد پستی"
                  value={pick(contractor, "postalCode", "PostalCode")}
                />
              </Grid>
              {(pick(contractor, "about", "About") ||
                pick(contractor, "description", "Description")) && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      mt: 1,
                      p: 2,
                      borderRadius: "12px",
                      bgcolor: alpha(contractorUi.primary, 0.04),
                      border: `1px solid ${alpha(contractorUi.primary, 0.08)}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: contractorUi.muted, fontWeight: 700 }}
                    >
                      درباره پیمانکار
                    </Typography>
                    <Typography sx={{ mt: 0.75, color: contractorUi.text, lineHeight: 1.9 }}>
                      {pick(contractor, "about", "About") ||
                        pick(contractor, "description", "Description")}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </InfoCard>
        </Grid>

        {/* Documents */}
        <Grid item xs={12} md={6}>
          <InfoCard title="مدارک بارگذاری‌شده" subtitle="فایل‌های هویتی و مجوزها">
            <Stack spacing={1.25}>
              {documents.map((doc) => (
                <DocumentRow
                  key={doc.name}
                  name={doc.name}
                  statusLabel={doc.statusLabel}
                  statusTone={doc.statusTone}
                  href={doc.href}
                />
              ))}
            </Stack>
          </InfoCard>
        </Grid>

        {/* Compliance */}
        <Grid item xs={12} md={6}>
          <InfoCard title="وضعیت انطباق و مجوزها" subtitle="چک‌لیست مدارک الزامی">
            <Stack spacing={1.25}>
              {complianceItems.map((item) => (
                <ComplianceItem key={item.label} label={item.label} ok={item.ok} />
              ))}
            </Stack>
            {pick(contractor, "registrationId", "RegistrationId") ? (
              <Box
                sx={{
                  mt: 2.5,
                  p: 2,
                  borderRadius: "12px",
                  bgcolor: alpha(contractorUi.success, 0.08),
                  border: `1px solid ${alpha(contractorUi.success, 0.16)}`,
                }}
              >
                <Typography variant="caption" sx={{ color: contractorUi.muted, fontWeight: 700 }}>
                  اطلاعات تأیید
                </Typography>
                <Typography sx={{ mt: 0.75, fontWeight: 700, color: contractorUi.text }}>
                  شناسه ثبت‌نام: {pick(contractor, "registrationId", "RegistrationId")}
                </Typography>
                <Typography sx={{ mt: 0.5, color: contractorUi.muted }}>
                  تاریخ تأیید: {formatDate(pick(contractor, "approvedAt", "ApprovedAt"))}
                </Typography>
              </Box>
            ) : null}
          </InfoCard>
        </Grid>

        {/* Address / Map */}
        <Grid item xs={12}>
          <InfoCard title="آدرس و موقعیت" subtitle="محل فعالیت پیمانکار">
            <MapPlaceholder address={address} />
          </InfoCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContractorInfoInformationPage;
